import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { talentService } from '../../../api/apiService';
import '../../../App.css';
import './TalentEditSimple.css';

/**
 * Edit talent for the simple list only: basic info + Agentur, AP, Gage, Lano ID + VAT/billing.
 * No related talents, no good/tryout/bad company relationships.
 */
function TalentEditSimple() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [talent, setTalent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    const fetchTalent = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const response = await talentService.getTalentById(id);
        setTalent(response.data);
      } catch (err) {
        console.error('Error fetching talent:', err);
        setError('Failed to load talent');
      } finally {
        setLoading(false);
      }
    };
    fetchTalent();
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    const next = type === 'checkbox' ? e.target.checked : value;
    setTalent(prev => ({ ...prev, [name]: next }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveMessage('');
    try {
      const original = await talentService.getTalentById(id).then(r => r.data);
      const payload = {};
      const set = (key, newVal, origVal) => {
        if (newVal !== origVal) payload[key] = newVal;
      };
      // First 4 fields are read-only, not sent on save
      set('Tagessatz', talent.dayRate || talent.Tagessatz, original.Tagessatz);
      set('Stundensatz', talent.hourlyRate || talent.Stundensatz, original.Stundensatz);
      set('Telefon', talent.phone || talent.Telefon, original.Telefon);
      set('Notiz_Setcast', talent.notes || talent.Notiz_Setcast, original.Notiz_Setcast);
      set('Agentur', talent.agentur || talent.Agentur, original.Agentur);
      set('AP', talent.ap != null ? talent.ap : talent.AP, original.AP);
   //   set('Gage', talent.gage || talent.Gage, original.Gage);
      set('Lano_ID', talent.lanoId || talent.Lano_ID, original.Lano_ID);
      set('vat_status', talent.vat_status, original.vat_status);
      set('tax_id', talent.tax_id, original.tax_id);
      set('vat_id', talent.vat_id, original.vat_id);
      set('bank_account', talent.bank_account, original.bank_account);
      set('country', talent.country, original.country);
      set('company_name', talent.company_name, original.company_name);
      set('agency_vat_status', talent.agency_vat_status, original.agency_vat_status);
      set('agency_tax_id', talent.agency_tax_id, original.agency_tax_id);
      set('agency_vat_id', talent.agency_vat_id, original.agency_vat_id);
      set('agency_bank_account', talent.agency_bank_account, original.agency_bank_account);
      set('agency_email', talent.agency_email, original.agency_email);
      set('agency_address', talent.agency_address, original.agency_address);
      set('address', talent.address, original.address);
      set('is_agency', !!(talent.is_agency === true || talent.is_agency === 1), !!(original.is_agency === true || original.is_agency === 1));
      set('agency_bill_separately', !!(talent.agency_bill_separately === true || talent.agency_bill_separately === 1), !!(original.agency_bill_separately === true || original.agency_bill_separately === 1));

      if (Object.keys(payload).length > 0) {
        await talentService.updateTalent(id, payload);
        setSaveMessage('Saved.');
      } else {
        setSaveMessage('No changes.');
      }
    } catch (err) {
      console.error('Error saving:', err);
      setError('Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) return <div className="content-wrapper page"><div className="content-wrapper"><p>Loading…</p></div></div>;
  if (error) return <div className="content-wrapper page"><div className="content-wrapper"><p className="error">{error}</p></div></div>;
  if (!talent) return <div className="content-wrapper page"><div className="content-wrapper"><p className="error">Talent not found</p></div></div>;

  return (
    <div className="content-wrapper page">
      <div className="content-wrapper">
        <h1>Edit talent (billing)</h1>
        {saveMessage && (
          <div className="talent-edit-simple-message success">
            {saveMessage}
          </div>
        )}
        <form onSubmit={handleSave}>
          <h2>Basic information</h2>
          <div className="form-group">
            <label>Name</label>
            <input type="text" readOnly value={talent.name || talent.Name || ''} className="talent-edit-readonly" />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input type="email" readOnly value={talent.email || talent.E_Mail || ''} className="talent-edit-readonly" />
          </div>
          <div className="form-group">
            <label>Function</label>
            <input type="text" readOnly value={talent.function || talent.Funktion || ''} className="talent-edit-readonly" />
          </div>
          <div className="form-group">
            <label>Type</label>
            <input type="text" readOnly value={talent.type || talent.Bereich || ''} className="talent-edit-readonly" />
          </div>
          <div className="form-group">
            <label>Day rate</label>
            <input type="text" name="dayRate" value={talent.dayRate || talent.Tagessatz || ''} onChange={handleInputChange} required />
          </div>
          <div className="form-group">
            <label>Hourly rate</label>
            <input type="text" name="hourlyRate" value={talent.hourlyRate || talent.Stundensatz || ''} onChange={handleInputChange} />
          </div>
          <div className="form-group">
            <label>Phone</label>
            <input type="text" name="phone" value={talent.phone || talent.Telefon || ''} onChange={handleInputChange} />
          </div>
          <div className="form-group">
            <label>Notes</label>
            <textarea name="notes" value={talent.notes || talent.Notiz_Setcast || ''} onChange={handleInputChange} rows={4} />
          </div>

          <h2 className="talent-edit-section-title">Agency / AP</h2>
          <div className="form-group talent-edit-checkbox">
            <label>
              <input
                type="checkbox"
                name="is_agency"
                checked={!!(talent.is_agency === true || talent.is_agency === 1)}
                onChange={handleInputChange}
              />
              {' '}Is agency
            </label>
            <small className="talent-edit-hint">
              When checked, this talent is treated as an agency: agency info and VAT are used on credit notes. You can bill all in one CN or separately (talent CN + agency provision CN).
            </small>
          </div>
          {(talent.is_agency === true || talent.is_agency === 1) && (
            <>
              <div className="form-group talent-edit-checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="agency_bill_separately"
                    checked={!!(talent.agency_bill_separately === true || talent.agency_bill_separately === 1)}
                    onChange={handleInputChange}
                  />
                  {' '}Bill separately (talent CN + agency provision CN)
                </label>
                <small className="talent-edit-hint">
                  When checked: first credit note = talent with talent VAT; second = agency provision (AP %) with agency VAT. When unchecked: one credit note to agency with all lines and agency VAT.
                </small>
              </div>
            </>
          )}
          <small className="talent-edit-hint talent-edit-hint-block">
            Agency details (name, VAT, bank, etc.) are stored per talent. If several talents use the same agency, fill or keep in sync on each talent.
          </small>
          <div className="form-group">
            <label>Agency</label>
            <input type="text" name="agentur" value={talent.agentur || talent.Agentur || ''} onChange={handleInputChange} />
          </div>
          <div className="form-group">
            <label>AP</label>
            <input type="text" name="ap" value={talent.ap != null ? talent.ap : (talent.AP ?? '')} onChange={handleInputChange} />
          </div>
 
          <div className="form-group">
            <label>Lano ID</label>
            <input type="text" name="lanoId" value={talent.lanoId || talent.Lano_ID || ''} onChange={handleInputChange} />
          </div>
          <div className="form-group">
            <label>Agency VAT status</label>
            <select name="agency_vat_status" value={talent.agency_vat_status || ''} onChange={handleInputChange}>
              <option value="">—</option>
              <option value="with_vat">With VAT (19 %)</option>
              <option value="without_vat">Without VAT</option>
            </select>
            <small className="talent-edit-hint">
              For agency/divided credit notes. Set &quot;With VAT&quot; when agency charges 19 %.
            </small>
          </div>
          <div className="form-group">
            <label>Agency Tax ID</label>
            <input type="text" name="agency_tax_id" value={talent.agency_tax_id || ''} onChange={handleInputChange} />
          </div>
          <div className="form-group">
            <label>Agency VAT ID</label>
            <input type="text" name="agency_vat_id" value={talent.agency_vat_id || ''} onChange={handleInputChange} />
          </div>
          <div className="form-group">
            <label>Agency bank account</label>
            <textarea name="agency_bank_account" value={talent.agency_bank_account || ''} onChange={handleInputChange} rows={2} />
          </div>
          <div className="form-group">
            <label>Agency email</label>
            <input type="email" name="agency_email" value={talent.agency_email || ''} onChange={handleInputChange} />
          </div>
          <div className="form-group">
            <label>Agency address</label>
            <textarea name="agency_address" value={talent.agency_address || ''} onChange={handleInputChange} rows={2} placeholder="Straße Hausnr., PLZ Ort, Land" />
            <small className="talent-edit-hint">Required on credit notes (German law). E.g.: Musterstraße 12, 20099 Hamburg</small>
          </div>

          <h2 className="talent-edit-section-title">Billing / VAT</h2>
          <div className="form-group">
            <label>VAT status</label>
            <select name="vat_status" value={talent.vat_status || ''} onChange={handleInputChange}>
              <option value="">—</option>
              <option value="with_vat">With VAT (19 %)</option>
              <option value="without_vat">Without VAT</option>
              <option value="kleinunternehmer">Kleinunternehmer</option>
              <option value="auslander">Ausländer (abroad)</option>
            </select>
            <small className="talent-edit-hint">
              When talent has no VAT and &quot;Agency VAT status&quot; above is With VAT (and AP is set), two credit notes are created automatically (talent + agency). With &quot;Is agency&quot; + &quot;Bill separately&quot; you get talent CN + agency provision CN.
            </small>
          </div>
          <div className="form-group">
            <label>Tax ID</label>
            <input type="text" name="tax_id" value={talent.tax_id || ''} onChange={handleInputChange} />
          </div>
          <div className="form-group">
            <label>VAT ID</label>
            <input type="text" name="vat_id" value={talent.vat_id || ''} onChange={handleInputChange} />
          </div>
          <div className="form-group">
            <label>Bank account</label>
            <textarea name="bank_account" value={talent.bank_account || ''} onChange={handleInputChange} rows={2} />
          </div>
          <div className="form-group">
            <label>Country</label>
            <input type="text" name="country" value={talent.country || ''} onChange={handleInputChange} placeholder="e.g. Germany" />
          </div>
          <div className="form-group">
            <label>Company name</label>
            <input type="text" name="company_name" value={talent.company_name || ''} onChange={handleInputChange} />
          </div>
          <div className="form-group">
            <label>Address (Adresse)</label>
            <textarea name="address" value={talent.address || ''} onChange={handleInputChange} rows={2} placeholder="Straße Hausnr., PLZ Ort, Land" />
            <small className="talent-edit-hint">Required on credit notes (German law). E.g.: Musterstraße 12, 20099 Hamburg</small>
          </div>

          <div className="button-group talent-edit-actions">
            <button type="submit" className="button primary" disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="button secondary"
              onClick={() => navigate('/admin/talent-list-simple')}
              disabled={isSaving}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default TalentEditSimple;
